import { localizeDate } from "@devforce/trailhead-client-locale";
import classNames from "classnames";
import { UAParser } from "ua-parser-js";
import { LightningElement, api, track, wire } from "lwc";
import { LabelsAdapter, LabelsAdapterValue } from "common/labelsAdapter";
import { attrToBool, BooleanAttr } from "common/helpers";
import * as locales from "./locales";
import { TRAILHEAD_EVENT_LOGIN } from "th/tbid";
import { Assessment, Question, SelectedAnswers, Option } from "./types";
import { validateResults } from "./validateAnswers";

/** Each letter of the alphabet for the lettering of options */
export const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const defaultAssessmentState: Assessment["assessment_state"] = {
  attempts: 0,
  completed: false,
  points_available: 100,
  points_available_next: 50,
  points_earned: 0,
  content_progress: 0,
  badge_url: "",
  content_title: "",
};

/** Presentational (API-agnostic) multiple option quiz component with feedback */
export default class EnhancedQuiz extends LightningElement {
  private completeAssessmentData: Assessment | undefined;
  private isDesktop = true;

  // Denotes when the user who loaded the quiz is not logged in, so they can't take the quiz.
  @api anonymous = false;

  // Disables submit button when the user still needs to connect their account to a third-party service (like Tableau)
  @api connectedAccountRequired = false;

  // Sets the quiz into a loading state (i.e. during submission)
  @api loading = false;

  // Sets the quiz in preview mode, which allows users to take the quiz without earning points
  @api preview = false;

  // When loaded in a mobile browser, disables the submit button and blurs the quiz with an error.
  @api desktopOnly = false;

  // Enables ability to retake the quiz
  @api retakeEnabled: BooleanAttr = false;

  /** Callback when the user submits
   * @param selectedAnswers - The selected answers
   *
   * Typically used for network calls to validate the user's answers
   */
  @api handleSubmit: (selectedAnswers: SelectedAnswers) => void = () => {};

  /** Callback when the user submits *and* successfully completes the quiz
   * @param assessmentState - The assessment state of the quiz
   *
   * Typically used for presenting the unit-completed modal to the user
   */
  @api handleComplete: (
    assessmentState: Assessment["assessment_state"]
  ) => void = () => {};

  @wire(LabelsAdapter, { locales, options: "$labelOptions" })
  private labels: LabelsAdapterValue = {};
  private get labelOptions() {
    return {
      completedDateLabel: {
        completedDate: localizeDate(
          this.assessmentState?.completed_time || new Date()
        ),
      },
      footerLabel_first: {
        second_attempt: this.assessmentState?.points_available_next,
        third_attempt: Math.floor(
          (this.assessmentState?.points_available_next ?? 0) / 2
        ),
      },
      footerLabel_second: {
        third_attempt: this.assessmentState?.points_available_next,
      },
      footerLabelincorrect_multiple: {
        numIncorrectQuestions: this.numIncorrectQuestions,
      },
      submitButtonLabel: {
        points: this.pointsAvailable,
      },
      pointsLabel: {
        points: this.pointsAvailable,
      },
      pointsEarnedLabel: {
        points: this.assessmentState?.points_earned,
      },
    };
  }

  /** The metadata for the quiz */
  @track
  private _assessment?: Assessment;

  @api
  get assessment(): Assessment | undefined {
    return this._assessment;
  }
  set assessment(assessment: Assessment | undefined) {
    if (assessment) {
      // Copies the assessment and sets default values
      const cachedAssessment = JSON.parse(
        JSON.stringify(assessment)
      ) as Assessment;
      cachedAssessment.assessment_state = Object.assign(
        {},
        defaultAssessmentState,
        assessment.assessment_state
      );
      if (this.preview) {
        this.completeAssessmentData = cachedAssessment;
        const incompleteAssessmentData = JSON.parse(
          JSON.stringify(cachedAssessment)
        ) as Assessment;
        incompleteAssessmentData.assessment_ui.questions.forEach((question) => {
          question.options = question.options.map(({ id, label }) => {
            return { id, label };
          });
        });
        this._assessment = incompleteAssessmentData;
      } else {
        this._assessment = cachedAssessment;
      }
    } else {
      this._assessment = assessment;
    }
    const questions = assessment?.assessment_ui.questions;
    if (questions) {
      // Count how many are incorrect
      let incorrectCount = 0;
      questions.forEach((question) => {
        if (this.isWrongOptionSelected(question)) {
          incorrectCount++;
        }
      });
      this.numIncorrectQuestions = incorrectCount;

      if (
        incorrectCount === 0 &&
        this.assessmentState.completed &&
        Object.keys(this.selectedAnswers).length > 0
      ) {
        this.completeQuiz(questions);
      }
    }
  }

  /**
   * indeces represent the index of the question and values represent if the last
   * submission was correct. if results is empty, the quiz has not been submitted.
   */
  @api results: boolean[] = [];

  /**
   * indeces represent the id of the question and values the id of the
   * currently selected answer. falsy values mean the question hasn't been answered
   */
  @track selectedAnswers: SelectedAnswers = {};

  /** The selected answers for the most recent submission */
  @track lastSubmission: Record<string, string> = {};

  @track activeQuestionIds = new Set<string>();
  @track numIncorrectQuestions: number = 0;
  @track isRetaking = false;

  connectedCallback(): void {
    const agent = new UAParser(window.navigator.userAgent);
    const device = agent.getDevice();
    this.isDesktop =
      device.type === undefined ||
      !["wearable", "mobile"].includes(device.type);
  }

  /** Holds the question API data and UI state for each question */
  get questionElements() {
    return this.questions.map((question, questionIndex) => {
      const toggleActive = () => {
        if (this.activeQuestionIds.has(question.id)) {
          this.activeQuestionIds.delete(question.id);
        } else {
          this.activeQuestionIds.add(question.id);
        }
        // Trigger UI updates
        this.activeQuestionIds = new Set([...this.activeQuestionIds]);
      };
      const handleKeyDown = (e: KeyboardEvent) => {
        switch (e.key) {
          case "Enter":
          case " ":
            toggleActive();
            break;
          default:
        }
      };
      return {
        data: question,
        number: questionIndex + 1,
        elementId: `question-${questionIndex}`,
        labelClass: this.questionLabelClass(question),
        optionsClass: this.questionOptionsClass(question),
        toggleActive,
        handleKeyDown,
        active: this.activeQuestionIds.has(question.id),
        chevronClass: this.questionChevronClass(question),
        options: this.questionOptions(question),
      };
    });
  }

  get questionsClass() {
    return classNames("questions", {
      assessment_completed: this.isAssessmentCompleted,
    });
  }

  get questions() {
    return this.assessment?.assessment_ui.questions ?? [];
  }

  get callToAction(): Assessment["call_to_action"] {
    return (
      this.assessment?.call_to_action ?? {
        text: this.labels.defaultCTA,
        url: "https://trailhead.salesforce.com/modules",
      }
    );
  }

  get ctaButtonClass() {
    return classNames("footer-button", {
      button_disabled: this.submitDisabled,
    });
  }

  get legacyRewards(): Assessment["legacy_rewards"] {
    return this.assessment?.legacy_rewards ?? [];
  }

  get assessmentState(): Assessment["assessment_state"] {
    return this.assessment?.assessment_state ?? defaultAssessmentState;
  }

  get isModuleCompleted() {
    return this.assessmentState?.content_progress === 100;
  }

  /** Has an answer been selected for each question? */
  get allQuestionsAnswered() {
    return this.questions.length === Object.keys(this.selectedAnswers).length;
  }

  /** Is the submit button disabled? */
  get submitDisabled() {
    return !this.allQuestionsAnswered || this.loading || this.allAnswersCorrect;
  }

  /** The text to display in the footer */
  get footerText() {
    if (this.loading) {
      return this.labels.loadingText;
    } else if (this.numIncorrectQuestions === 1) {
      return this.labels.footerLabelincorrect_single;
    } else if (this.numIncorrectQuestions > 1) {
      return this.labels.footerLabelincorrect_multiple;
    } else if (this.assessmentState?.attempts === 0) {
      return this.labels.footerLabel_first;
    } else if (this.assessmentState?.attempts === 1) {
      return this.labels.footerLabel_second;
    } else {
      return "";
    }
  }

  /** What classes to apply to the footer text (i.e. red, green) */
  get footerTextClass() {
    return classNames("footer-text", {
      "footer-text_incorrect": this.isAnyOptionIncorrect,
      "footer-text_success": this.allAnswersCorrect,
    });
  }

  /** Did the user finish loading and get results? */
  get didGetResults() {
    return !this.loading;
  }

  /** Are all answers correct? */
  get allAnswersCorrect() {
    return this.didGetResults && this.isAssessmentCompleted;
  }

  /** Is an incorrect option for any question selected? */
  get isAnyOptionIncorrect() {
    return this.questions.some((question) =>
      this.isQuestionIncorrect(question)
    );
  }

  get isAssessmentCompleted() {
    return this.assessmentState.completed && !this.isRetaking;
  }

  get showDesktopOnlyError() {
    return this.desktopOnly && !this.isDesktop;
  }

  get showRetakeCta() {
    return (
      this.isAssessmentCompleted &&
      attrToBool(this.retakeEnabled) &&
      !this.isRetaking
    );
  }

  get pointsAvailable(): number {
    const points = this.assessmentState?.points_available || 100;
    // Manually recalculate the number of points available when we're in preview mode,
    // since no server-side calls are made while we're in preview mode.
    if (
      this.preview &&
      this.completeAssessmentData &&
      this.assessmentState.attempts > 0
    ) {
      return points / Math.min(this.assessmentState.attempts * 2, 4);
    }
    return points;
  }

  /** Classes for the question labels depending on state */
  questionLabelClass(question: Question) {
    return classNames("question-label", {
      "question-label_incorrect": this.isQuestionIncorrect(question),
    });
  }

  questionOptions(question: Question) {
    return question.options.map((option, optionIndex) => ({
      data: option,
      id: option.id,
      showFeedback: this.shouldShowFeedback(question, option),
      correctnessLabel: this.optionFeedbackHeading(option),
      letter: alphabet[optionIndex],
      letterSymbol: option.correct ? "approval" : "close",
      labelClass: this.optionLabelClass(question, option),
      optionClass: this.optionClass(question, option),
      disabled: this.isOptionDisabled(question),
      feedbackTextClass: this.optionFeedbackTextClass(option),
    }));
  }

  /** Classes for the list of options under each question */
  questionOptionsClass(question: Question) {
    return classNames("options", {
      active:
        this.activeQuestionIds.has(question.id) || !this.isAssessmentCompleted,
    });
  }

  /** Classes for the option labels depending on state */
  optionLabelClass(question: Question, option: Option) {
    return classNames("option-label", {
      "option-label_incorrect": this.optionIsIncorrect(question, option),
      "option-label_correct": option.correct,
    });
  }

  optionClass(question: Question, option: Option) {
    return classNames("option", {
      selected: this.selectedAnswers[question.id] === option.id,
      incorrect:
        this.optionIsIncorrect(question, option) &&
        this.selectedAnswers[question.id] === option.id,
      correct: option.correct,
    });
  }

  /** Classes for the option labels depending on state */
  optionFeedbackTextClass(option: Option) {
    return classNames("option-feedback__text", {
      "option-feedback__text_incorrect": option.correct === false,
      "option-feedback__text_correct": option.correct === true,
    });
  }

  questionChevronClass(question: Question) {
    return classNames("question-chevron", {
      active: this.activeQuestionIds.has(question.id),
    });
  }

  optionFeedbackHeading(option: Option) {
    if (option.correct === false) {
      return this.labels.incorrectLabel;
    } else if (option.correct === true) {
      return this.labels.correctLabel;
    } else {
      return;
    }
  }

  /** Was the last submission for this question incorrect? */
  isQuestionIncorrect(question: Question) {
    return (
      !this.loading &&
      this.isWrongOptionSelected(question) &&
      !this.isQuestionCompleted(question)
    );
  }

  /** Does the question have one (and only one) option with correct === false? */
  isWrongOptionSelected(question: Question) {
    const selectedOption = question.options.find(
      (option) => option.id === this.selectedAnswers[question.id]
    );
    return selectedOption?.correct === false;
  }

  /** Has the question been correctly answered in the past? */
  isQuestionCompleted(question: Question) {
    // Does every option in enhancedOption contain a 'correct' and 'feedback' key?
    return question.options.every(
      (option) => option.correct !== undefined && option.feedback
    );
  }

  /** Is the given option for the question incorrect? */
  isOptionIncorrect(question: Question, option: Option) {
    return this.isQuestionIncorrect(question) && option.correct === false;
  }

  /** Is the given option disabled? */
  isOptionDisabled(question: Question) {
    return (
      this.loading ||
      this.isQuestionCorrect(question) ||
      this.anonymous ||
      this.connectedAccountRequired
    );
  }

  /** Is the option for a question correct? */
  isOptionCorrect(question: Question, option: Option) {
    return this.isQuestionCorrect(question) && option.correct === true;
  }

  /** Is the question correct? */
  isQuestionCorrect(question: Question) {
    return (
      this.didGetResults &&
      question.options.some((option) => option.correct === true)
    );
  }

  /** Should feedback be shown? */
  shouldShowFeedback(question: Question, option: Option) {
    // Show feedback if the assessment is complete, or if the option was selected in the last submission
    if (option.correct !== undefined) {
      if (
        this.isAssessmentCompleted ||
        (this.lastSubmission[question.id] === option.id &&
          this.selectedAnswers[question.id] === option.id)
      ) {
        return true;
      }
    }
    return false;
  }

  /** When a new option is selected for a question */
  handleInputChange(event: Event) {
    const target = event.target as HTMLInputElement;

    const optionID = target.value;
    const questionID = target.dataset.questionId || "";

    // Creates a new copy to ensure that lwc notices the change
    // and re-renders the component
    this.selectedAnswers = {
      ...this.selectedAnswers,
      [questionID]: optionID,
    };

    delete this.lastSubmission[questionID];

    this.dispatchEvent(
      new CustomEvent("select", {
        detail: { selectedAnswers: this.selectedAnswers },
      })
    );
  }

  /** When the user clicks submit */
  onSubmit() {
    if (this.submitDisabled) return;
    this.lastSubmission = Object.assign({}, this.selectedAnswers);
    if (this.preview && this.assessment && this.completeAssessmentData) {
      this.loading = true;
      setTimeout(() => {
        if (this.assessment && this.completeAssessmentData) {
          this._assessment = validateResults(
            this.assessment,
            this.completeAssessmentData,
            this.selectedAnswers
          );
          this.loading = false;
        }
      }, 750);
    } else {
      this.dispatchEvent(
        new CustomEvent("submit", {
          detail: { selectedAnswers: this.selectedAnswers },
        })
      );
    }
  }

  /** When the user clicks the button to login (the CTA when user has not logged in) */
  handleLogin() {
    document.dispatchEvent(new CustomEvent(TRAILHEAD_EVENT_LOGIN));
  }

  startRetake() {
    if (this.retakeEnabled) {
      this.dispatchEvent(new CustomEvent("retakestart"));
      this.isRetaking = true;
      this.selectedAnswers = {};
      const queriedElement = this.template.querySelector(".quiz-container");
      const form = queriedElement?.querySelector("form");
      form?.reset();
      // Reset the state of the assessment
      // Remove assessment answers
      this.assessment?.assessment_ui.questions.forEach((question) => {
        question.options.forEach((option) => {
          delete option.correct;
        });
      });
    }
  }

  private optionIsIncorrect(question: Question, option: Option) {
    return (
      option.correct === false &&
      (this.lastSubmission[question.id] === option.id ||
        this.isAssessmentCompleted)
    );
  }

  private completeQuiz(questions: Question[]) {
    if (this.isRetaking) {
      this.isRetaking = false;
      // Ensure all the answers are expanded for every question
      questions.forEach((question) => {
        this.activeQuestionIds.add(question.id);
      });
      this.dispatchEvent(new CustomEvent("retakecomplete"));
    } else {
      this.dispatchEvent(
        new CustomEvent("complete", {
          detail: {
            assessment_state: this.assessmentState,
            call_to_action: this.callToAction,
            legacy_rewards: this.legacyRewards,
          },
        })
      );
    }
  }
}
